import { z } from 'zod';
const scalarSchema = (typeName) => {
    switch (typeName) {
        case 'String':
        case 'ID':
        case 'Date':
        case 'DateTime':
            return z.string();
        case 'Boolean':
            return z.boolean();
        case 'Int':
            return z.number().int();
        case 'Float':
            return z.number().finite();
        default:
            return z.any().refine(value => value !== undefined);
    }
};
const applySelectAndRange = (schema, config) => {
    const choices = config.state?.selectChoices;
    if (choices != null && choices.length > 0) {
        const values = choices.map(choice => choice.value);
        schema = z.enum(values);
    }
    if (config.state?.min != null || config.state?.max != null) {
        let numeric = z.number();
        if (config.state?.min != null)
            numeric = numeric.min(config.state.min);
        if (config.state?.max != null)
            numeric = numeric.max(config.state.max);
        schema = numeric;
    }
    return schema;
};
const isOptionalField = (config) => {
    if (config.kind !== 'INPUT_VALUE' && config.kind !== 'FIELD') {
        return config.isNullable !== false;
    }
    if (config.isNullable !== false)
        return true;
    return config.kind === 'INPUT_VALUE' && config.defaultValue != null;
};
const acceptsNull = (config) => {
    if (config.kind === 'INPUT_VALUE' || config.kind === 'FIELD') {
        return config.ofType.isNullable === true;
    }
    return config.isNullable === true;
};
export const toZodSchema = (config, registry) => {
    const memo = new Map();
    const resolveReference = (typeName) => {
        const resolved = registry?.find(item => item.typeName === typeName);
        if (resolved == null) {
            throw new Error(`unresolved type reference "${typeName}"`);
        }
        return resolved;
    };
    const memoKey = (current) => current.kind === 'TYPE_REFERENCE'
        ? `ref:${current.typeName}`
        : `${current.kind}:${current.typeName}`;
    const build = (current) => {
        if (current.kind === 'TYPE_REFERENCE') {
            const cached = memo.get(memoKey(current));
            if (cached != null)
                return cached;
            const lazy = z.lazy(() => build(resolveReference(current.typeName)));
            memo.set(memoKey(current), lazy);
            return lazy;
        }
        if (current.kind === 'INPUT_OBJECT' || current.kind === 'OBJECT') {
            const cached = memo.get(memoKey(current));
            if (cached != null)
                return cached;
            const lazy = z.lazy(() => {
                const shape = {};
                for (const field of current.fields) {
                    let fieldSchema = build(field);
                    if (isOptionalField(field)) {
                        fieldSchema = fieldSchema.optional();
                    }
                    shape[field.key] = fieldSchema;
                }
                return z.object(shape).passthrough();
            });
            memo.set(memoKey(current), lazy);
            return lazy;
        }
        if (current.kind === 'INPUT_VALUE' || current.kind === 'FIELD') {
            let schema = applySelectAndRange(build(current.ofType), current);
            if (acceptsNull(current))
                schema = schema.nullable();
            return schema;
        }
        if (current.kind === 'LIST') {
            return z.array(build(current.ofType));
        }
        if (current.kind === 'SCALAR') {
            return applySelectAndRange(scalarSchema(current.typeName), current);
        }
        if (current.kind === 'ENUM') {
            const names = current.enumValues.map(value => value.name);
            if (names.length === 0)
                return z.never();
            return z.enum(names);
        }
        if (current.kind === 'UNION') {
            const members = current.possibleTypes.map(build);
            if (members.length === 0)
                return z.never();
            if (members.length === 1)
                return members[0];
            return z.union(members);
        }
        return z.unknown();
    };
    const schema = build(config);
    return isOptionalField(config) ? schema.optional() : schema;
};
//# sourceMappingURL=DataTypeConfig.zod.js.map